// TODO: feature/templates - review this file
import { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useMerchant,
  usePaymentSettings,
  canUpdatePaymentRequestTemplate,
} from "@auth";
import { useAccountSelection } from "@hooks";
import { Modal, Button } from "@components";
import { cn } from "@utils";
import { createPaymentRequestTemplate } from "@api";
import { usePaymentRequestTemplatesContext } from "@context/PaymentRequestTemplatesContext";
import { useBatchWorkflow } from "../PaymentRequestDrawers/hooks";
import { useEditorState, useDerivedData } from "./hooks";
import TemplateSelectStep from "./components/TemplateSelectStep";
import PreviewEditorStep from "./components/PreviewEditorStep";
import ConfirmSendStep from "./components/ConfirmSendStep";
import InvalidAccountsReviewStep from "./components/InvalidAccountsReviewStep";
import SaveAsTemplateModal from "./components/SaveAsTemplateModal";
import {
  configToTemplate,
  buildBatchMeta,
} from "./utils/buildSuggestionRequest";

const BatchTemplateRequestModal = ({
  isOpen,
  onClose,
  customerData: customerDataProp,
  onComplete,
  skipTemplateSelect = false,
  requestConfig = null,
  // Accept-an-action mode: when set, the confirm CTA reads "Accept" and routes the
  // edited values here instead of sending a batch — the caller turns them into the
  // action accept call's step amendments. The email channel is locked on (the
  // payment step's schema requires a recipient email).
  onAcceptAction = null,
  acceptPending = false,
  // Read-only preview: render the seeded email/SMS with no edit, review, or send
  // affordances — just a Close. Used to inspect a scheduled step's message.
  readOnly = false,
  // Overrides the preview header in read-only mode (e.g. "Instalment 1 of 4"); falls back to the
  // template name when null.
  readOnlyTitle = null,
}) => {
  const customerData = useMemo(
    () =>
      Array.isArray(customerDataProp)
        ? customerDataProp
        : customerDataProp
          ? [customerDataProp]
          : [],
    [customerDataProp],
  );
  const { merchant, hasPermission } = useMerchant();
  const { paymentSettings } = usePaymentSettings();
  const canEditTemplates = hasPermission(canUpdatePaymentRequestTemplate);
  const navigate = useNavigate();
  const {
    phase,
    rows,
    totals,
    progress,
    initialiseRowsWithSendStatus,
    runBatch,
    resetState,
  } = useBatchWorkflow();

  /* ----------------------- form ----------------------- */
  const {
    control,
    formState: { errors },
    reset,
    watch,
    getValues,
    setValue,
  } = useForm();

  const formValues = watch();
  const watchedInvoices = formValues.invoicesRequested;
  const watchedMandatory = formValues.mandatory;
  const watchedPdf = formValues.attachPdfStatement;
  const watchedCustomAmount = formValues.customAmount;
  const watchedCardPayment = formValues.allowCardPayment;
  const watchedPaymentProfile = formValues.paymentProfileId;

  /* ----------------------- hooks ---------------------- */
  const {
    templates,
    loading: templatesLoading,
    refresh: refreshTemplates,
  } = usePaymentRequestTemplatesContext();

  const editor = useEditorState({
    reset,
    getValues,
    setValue,
    formValues,
    watchedInvoices,
    watchedMandatory,
    watchedPdf,
    watchedCardPayment,
    watchedPaymentProfile,
  });

  /* ----------------------- local state ---------------- */
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [savingAs, setSavingAs] = useState(false);
  const [previewCustomerIdx, setPreviewCustomerIdx] = useState(0);

  /* --------------- account selection --------- */
  const accountSelection = useAccountSelection({ idKey: "customerId" });
  const selectionInitRef = useRef(null);

  /* ----------------------- derived data --------------- */
  const invoicesRequested = watchedInvoices ?? "Overdue";
  const isCustom = invoicesRequested === "Custom";
  const isSpecific = invoicesRequested === "SpecificInvoices";
  const isSingleCustomer = customerData?.length === 1;

  const {
    preparedRows,
    grossAmount,
    sendableAmount,
    previewTokens,
    sendableCount,
    unsendableCount,
    partialCount,
  } = useDerivedData({
    customerData,
    invoicesRequested,
    isCustom,
    isSpecific,
    watchedCustomAmount,
    smsEnabled: editor.smsEnabled,
    emailEnabled: editor.emailEnabled,
    previewCustomerIdx,
    rows,
    isSingleCustomer,
  });

  /* ------------------- initialise workflow ------------ */
  useEffect(() => {
    if (!preparedRows.length || !editor.selectedTemplate) return;
    initialiseRowsWithSendStatus(preparedRows, {
      sms: editor.smsEnabled,
      email: editor.emailEnabled,
    });
  }, [
    preparedRows,
    editor.smsEnabled,
    editor.emailEnabled,
    editor.selectedTemplate,
  ]);

  /* ----- skip select: seed the preview from the config prop, falling back
     to the first template when no config is supplied ----- */
  useEffect(() => {
    if (!skipTemplateSelect || !isOpen || editor.step !== "select") return;

    if (requestConfig) {
      editor.initFromTemplate(
        configToTemplate(requestConfig),
        requestConfig.customAmount != null
          ? { customAmount: requestConfig.customAmount }
          : undefined,
      );
      return;
    }

    if (!templatesLoading && templates?.length) {
      editor.initFromTemplate(templates[0]);
    }
  }, [
    skipTemplateSelect,
    isOpen,
    editor.step,
    requestConfig,
    templatesLoading,
    templates,
  ]);

  /* ------------------- step navigation ---------------- */
  const totalIssueCount = unsendableCount + partialCount;

  const handleReview = () => {
    if (totalIssueCount > 0) {
      editor.setStep("review-errors");
    } else {
      editor.setStep("confirm");
    }
  };

  const handleBackToPreview = () => {
    if (totalIssueCount > 0) {
      editor.setStep("review-errors");
    } else {
      editor.setStep("preview");
    }
  };

  /* ------------------- handlers ----------------------- */
  const handleCreateTemplate = () => {
    handleClose();
    navigate("/settings/templates", { state: { openCreate: true } });
  };

  const buildMessages = (values) => {
    const messages = [];
    if (editor.emailEnabled) {
      messages.push({
        communicationMethod: "Email",
        content: {
          subject: values.emailSubject,
          title: values.emailTitle,
          mainText: values.emailMainText,
          actionArea: {
            title: values.emailActionTitle,
            description: "",
            amount: values.amountDisplay,
            buttonText: values.emailButtonText,
          },
        },
      });
    }
    if (editor.smsEnabled) {
      messages.push({
        communicationMethod: "Sms",
        content: { text: values.smsText },
      });
    }
    return messages;
  };

  const handleSaveAsConfirm = async (name) => {
    setSavingAs(true);
    try {
      const values = getValues();
      const messages = buildMessages(values);
      const paymentRequirement = values.mandatory ? "Mandatory" : "Flexible";
      const reqInvoices =
        values.invoicesRequested === "Custom" ? null : values.invoicesRequested;

      const newTemplate = await createPaymentRequestTemplate(
        name,
        reqInvoices,
        paymentRequirement,
        values.attachPdfStatement,
        messages,
        values.allowCardPayment,
        values.paymentProfileId ?? null,
      );

      if (newTemplate?.id) {
        refreshTemplates();
        editor.applyNewTemplate(newTemplate);
      }

      setShowSaveAs(false);
    } finally {
      setSavingAs(false);
    }
  };

  const handleConfirmSend = async (selectedRows, copies = null) => {
    const values = getValues();
    const strategy = { sms: editor.smsEnabled, email: editor.emailEnabled };

    if (onAcceptAction) {
      onAcceptAction({ values, strategy, account: selectedRows[0], cc: copies?.cc, bcc: copies?.bcc });
      return;
    }

    await runBatch({
      strategy,
      rowsOverride: selectedRows,
      meta: buildBatchMeta({
        values,
        invoicesRequested,
        templateName: editor.selectedTemplate?.name,
        templateId: editor.selectedTemplate?.id,
      }),
      paymentSettings,
    });
  };

  // Accept mode locks the email channel on: the amended step must keep a recipient
  // email (schema-required), so only SMS may be toggled.
  const handleToggleChannel = (channel) => {
    if (onAcceptAction && channel === "email" && editor.emailEnabled) return;
    editor.handleToggleChannel(channel);
  };

  const handleDone = () => {
    onComplete?.();
    handleClose();
  };

  const handleClose = () => {
    onClose?.();
  };

  /* ------------------- reset on close ----------------- */
  useEffect(() => {
    if (!isOpen) {
      resetState();
      editor.resetEditorState();
      setShowSaveAs(false);
      setSavingAs(false);
      accountSelection.resetSelection();
      selectionInitRef.current = null;
    }
  }, [isOpen, resetState]);

  /* ------------------- modal config per step ---------- */
  const templateName = editor.selectedTemplate?.name ?? "Template";

  const modalTitles = {
    select: "Select a template",
    preview: (
      <div className="flex flex-col gap-2 w-full">
        <span className="text-base font-semibold text-zinc-900 whitespace-nowrap">
          {readOnlyTitle ?? (editor.isEdited ? `${templateName} (edited)` : templateName)}
        </span>
        {(!editor.previewMode || editor.isEdited) && (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2 py-2 text-xs font-medium text-blue-800 ring-1 ring-inset ring-blue-200 w-full">
            <span className="flex size-3 shrink-0 items-center justify-center rounded-full bg-blue-800 text-[10px] font-bold leading-none text-white">
              i
            </span>
            Changes apply to this request only
          </span>
        )}
      </div>
    ),
    "review-errors": `Issues found`,
    confirm: templateName,
  };

  const modalSize =
    editor.step === "select"
      ? "md-lg"
      : editor.step === "preview" &&
          (!editor.previewMode || (editor.smsEnabled && editor.emailEnabled))
        ? "lg"
        : editor.step === "confirm"
          ? "md-lg"
          : "md";

  const fullHeight = editor.step === "preview";

  return (
    <>
      <Modal
        title={modalTitles[editor.step]}
        isOpen={isOpen}
        onClose={handleClose}
        size={modalSize}
        fullHeight={fullHeight}
        maxHeight="780px"
        preventBackdropClose
        bodyClassName={cn("overflow-hidden flex flex-col bg-white")}
      >
        {editor.step === "select" && (
          <>
            <TemplateSelectStep
              templates={templates}
              templatesLoading={templatesLoading}
              onSelect={editor.initFromTemplate}
              isSingleCustomer={isSingleCustomer}
            />
            <div className="flex shrink-0 items-center justify-between border-t border-zinc-200 bg-white px-5 py-3">
              <Button onClick={handleClose} color="secondary" size="md">
                Cancel
              </Button>
              {canEditTemplates && (
                <Button onClick={handleCreateTemplate} color="link" size="md">
                  Create a Template
                </Button>
              )}
            </div>
          </>
        )}

        {editor.step === "preview" && editor.selectedTemplate && (
          <>
            <PreviewEditorStep
              control={control}
              errors={errors}
              watch={watch}
              getValues={getValues}
              setValue={setValue}
              merchant={merchant}
              activeTab={editor.activeTab}
              setActiveTab={editor.setActiveTab}
              previewMode={editor.previewMode}
              emailEnabled={editor.emailEnabled}
              smsEnabled={editor.smsEnabled}
              invoicesRequested={invoicesRequested}
              watchedMandatory={watchedMandatory}
              invoiceEdited={editor.invoiceEdited}
              pdfEdited={editor.pdfEdited}
              cardEdited={editor.cardEdited}
              paymentProfileEdited={editor.paymentProfileEdited}
              channelEdited={editor.channelEdited}
              isEdited={editor.isEdited}
              hasUnappliedChanges={editor.hasUnappliedChanges}
              totalAmount={grossAmount}
              previewTokens={previewTokens}
              preparedRows={preparedRows}
              previewCustomerIdx={previewCustomerIdx}
              onPreviewCustomerChange={setPreviewCustomerIdx}
              onToggleChannel={handleToggleChannel}
              onBack={editor.handleBackToSelect}
              onResetEdits={editor.handleResetEdits}
              onSend={handleReview}
              onEnterEdit={editor.handleEnterEdit}
              onCancelEdit={editor.handleCancelEdit}
              onDoneEdit={editor.handleDoneEdit}
              onResetInEditMode={editor.handleResetInEditMode}
              onSaveAsTemplate={() => setShowSaveAs(true)}
              canEditTemplates={canEditTemplates}
              isSingleCustomer={isSingleCustomer}
              hideBack={skipTemplateSelect}
              readOnly={readOnly}
              onClose={handleClose}
            />
          </>
        )}

        {editor.step === "review-errors" && (
          <InvalidAccountsReviewStep
            rows={rows}
            smsSelected={editor.smsEnabled}
            emailSelected={editor.emailEnabled}
            sendableCount={sendableCount}
            sendableAmount={sendableAmount}
            invoicesRequested={invoicesRequested}
            customAmount={watchedCustomAmount}
            onBack={() => editor.setStep("preview")}
            onProceed={() => editor.setStep("confirm")}
            isSingleCustomer={isSingleCustomer}
          />
        )}

        {editor.step === "confirm" && (
          <ConfirmSendStep
            rows={rows}
            phase={phase}
            totals={totals}
            currentAmount={progress * sendableAmount}
            invoicesRequested={invoicesRequested}
            isCustom={isCustom}
            customAmount={watchedCustomAmount}
            control={control}
            watch={watch}
            smsSelected={editor.smsEnabled}
            emailSelected={editor.emailEnabled}
            onSend={handleConfirmSend}
            onBack={handleBackToPreview}
            onDone={handleDone}
            isSingleCustomer={isSingleCustomer}
            emailEnabled={editor.emailEnabled}
            selection={accountSelection}
            initializedRef={selectionInitRef}
            initialContacts={
              requestConfig
                ? {
                    email: requestConfig.contactEmail ?? null,
                    phone: requestConfig.contactPhone ?? null,
                    cc: requestConfig.cc ?? [],
                    bcc: requestConfig.bcc ?? [],
                  }
                : null
            }
            customer={customerData[0] ?? null}
            acceptMode={!!onAcceptAction}
            acceptPending={acceptPending}
          />
        )}
      </Modal>

      <SaveAsTemplateModal
        isOpen={showSaveAs}
        onClose={() => setShowSaveAs(false)}
        onSave={handleSaveAsConfirm}
        loading={savingAs}
        defaultName={
          editor.selectedTemplate?.name
            ? `${editor.selectedTemplate.name} (copy)`
            : ""
        }
      />
    </>
  );
};

export default BatchTemplateRequestModal;
